import { brl, qtd } from '../lib/formato';
import { nomeDoItemVendido } from '../lib/coringa';

/**
 * O carrinho SEMPRE À VISTA da tela de Venda (pedido do dono, 17/08/2026:
 * "quando você adiciona um item, ele vai lá para o final da pesquisa").
 *
 * Duas formas, um componente:
 *  - LATERAL (telas largas): coluna direita, sticky — a busca rola à esquerda
 *    e o carrinho fica parado, com total e o botão que desce ao fechamento.
 *  - BARRA (celular, a realidade do balcão): faixa fixa acima do nav com
 *    "N itens · total · Fechar" — o carrinho deixa de sumir atrás da busca.
 *
 * Ele NÃO edita quantidade nem preço: isso mora na lista completa de itens,
 * que continua onde sempre esteve. Aqui é visão e atalho — duplicar os
 * controles seria duplicar as regras deles (reserva, coringa, teto).
 */
export interface ItemResumo {
  chave: string;
  descricao: string;
  descricao_avulsa?: string;
  quantidade: number;
  preco_unit: number;
  avulsa: boolean;
  /** Onde buscar a peça (06/10/2026): o código do lugar principal, ou nada. */
  lugar?: string | null;
}

export function CarrinhoLateral({ itens, total, aoRemover, aoFechar, rotuloFechar }: {
  itens: ItemResumo[];
  total: number;
  aoRemover: (chave: string) => void;
  aoFechar: () => void;
  /** "Fechar venda" por padrão; a aba Orçar manda "Emitir orçamento". */
  rotuloFechar?: string;
}) {
  if (itens.length === 0) {
    return (
      <aside className="cartao hidden p-4 text-sm text-dim lg:block">
        O carrinho está vazio — bipe ou busque uma peça.
      </aside>
    );
  }
  return (
    <aside className="cartao hidden max-h-[calc(100vh-8rem)] flex-col p-4 lg:flex"
           aria-label="Carrinho">
      <p className="rotulo mb-2">Carrinho · {itens.length} {itens.length === 1 ? 'item' : 'itens'}</p>
      <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto">
        {itens.map((i) => (
          <li key={i.chave} className="flex items-baseline justify-between gap-2 text-xs">
            <span className="min-w-0 truncate">
              <span className="numero text-mute">{qtd(i.quantidade)}× </span>
              {nomeDoItemVendido(i)}
              {i.avulsa && <span className="ml-1 uppercase text-dim">div.</span>}
              {i.lugar && <span className="numero ml-1.5 text-[0.6875rem] text-dim">{i.lugar}</span>}
            </span>
            <span className="flex shrink-0 items-baseline gap-2">
              <span className="numero">{brl(i.quantidade * i.preco_unit)}</span>
              {/* Tirar é texto, não sinal: o vermelho é do erro (Grafite). */}
              <button type="button" className="px-1 text-mute hover:text-paper" aria-label={`Remover ${i.descricao}`}
                      onClick={() => aoRemover(i.chave)}>×</button>
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-3 border-t border-edge pt-3">
        <div className="flex items-baseline justify-between">
          <span className="rotulo">Total</span>
          <span className="numero text-2xl font-medium text-paper" data-total-do-carrinho="">{brl(total)}</span>
        </div>
        {/* Secundário: ele só DESCE até o fechamento. O primário da tela é o
            "Confirmar venda" de lá — dois botões claros seriam nenhum. */}
        <button type="button" className="botao-secundario mt-2 w-full py-2.5 text-sm"
                onClick={aoFechar}>
          {rotuloFechar ?? 'Fechar venda'} ↓
        </button>
      </div>
    </aside>
  );
}

/** A faixa do celular: acima do nav (que é fixed bottom-0), só com carrinho. */
export function BarraCarrinho({ itens, total, aoFechar, rotuloFechar }: {
  itens: ItemResumo[];
  total: number;
  aoFechar: () => void;
  rotuloFechar?: string;
}) {
  if (itens.length === 0) return null;
  return (
    <button
      type="button"
      onClick={aoFechar}
      className="fixed inset-x-0 bottom-[calc(3.75rem+env(safe-area-inset-bottom))] z-20 min-h-11 border-t border-edge-forte bg-panel px-4 py-2.5 text-left shadow-lg print:hidden lg:hidden"
      aria-label="Ir para o fechamento da venda"
    >
      <span className="mx-auto flex max-w-5xl items-baseline justify-between">
        <span className="text-xs text-mute">
          {itens.length} {itens.length === 1 ? 'item' : 'itens'} no carrinho
        </span>
        <span className="text-sm font-semibold text-paper">
          <span className="numero">{brl(total)}</span> · {rotuloFechar ?? 'Fechar'} ↓
        </span>
      </span>
    </button>
  );
}
